import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  LockOutlined as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { useAuth } from '../context/AuthContext'

function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(email, password)

    if (result.success) {
      navigate('/')
    } else {
      setError(result.message)
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        // Fondo sutil: un solo color de marca, sin degradados saturados
        backgroundImage: (t) =>
          `radial-gradient(60rem 40rem at 50% -10%, ${t.palette.primary.main}14, transparent 70%)`,
        px: 2,
        py: 'calc(24px + env(safe-area-inset-top, 0px))',
        pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <Container maxWidth="xs" disableGutters>
        {/* Marca fuera de la tarjeta: menos ruido dentro del formulario */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 2,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Sistema ATS
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            SRI Ecuador · Anexo Transaccional Simplificado
          </Typography>
        </Box>

        <Paper elevation={1} sx={{ borderRadius: 3, p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Iniciar sesión
          </Typography>

          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Correo electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              // Teclado de email en móvil y autocompletado nativo
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              sx={{ mb: 2.5 }}
            />

            <TextField
              fullWidth
              id="password"
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" thickness={5} /> : null
              }
              sx={{ mt: 3.5 }}
            >
              {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
            </Button>
          </Box>
        </Paper>

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          color="text.secondary"
          sx={{ mt: 3 }}
        >
          Sistema multiempresa de reportes tributarios
        </Typography>
      </Container>
    </Box>
  )
}

export default Login
